import {
  CountryEnum,
  FormInput,
  FormSelect,
  PaymentProcessorEnum,
  PrimaryButton,
  getPaymentProcessorTitle,
  getSingleCountry,
} from '@systemeio/ui'
import HelpCenterLink from 'modules/profile/payment-gateways/components/help-center-link'
import { useConnectedPaymentIntegrations } from 'modules/profile/payment-gateways/hooks/use-connected-payment-integrations'
import { mercadoPagoConnect } from 'modules/profile/payment-gateways/integrations/mercadopago/api'
import {
  MercadoPagoCreateIntegrationInterface,
  MercadoPagoIntegrationErrorsInterface,
} from 'modules/profile/payment-gateways/integrations/mercadopago/types'
import { getMercadoPagoHelpPageLink } from 'modules/profile/payment-gateways/integrations/mercadopago/utils'
import React, { useState } from 'react'
import CreateModal from 'shared/components/create-modal'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { fetchWithErrors } from 'shared/utils/fetch-with-errors'

const defaultValues: MercadoPagoCreateIntegrationInterface = {
  publicKey: '',
  accessToken: '',
  country: undefined,
}

const defaultErrors: MercadoPagoIntegrationErrorsInterface = {
  publicKey: '',
  accessToken: '',
  country: '',
}

export const mercadoPagoCountryData: Array<
  | CountryEnum.Brazil
  | CountryEnum.Colombia
  | CountryEnum.Mexico
  | CountryEnum.Argentina
  | CountryEnum.Chile
> = [
  CountryEnum.Brazil,
  CountryEnum.Colombia,
  CountryEnum.Mexico,
  CountryEnum.Argentina,
  CountryEnum.Chile,
]

const MercadoPagoCreateModal = () => {
  const { t } = useLocoTranslation()
  const [opened, setOpened] = useState(false)
  const [errors, setErrors] = useState<MercadoPagoIntegrationErrorsInterface>(defaultErrors)
  const { user } = useUser()

  const { mutatePaymentIntegrations } = useConnectedPaymentIntegrations()

  const onCreate = async (data: MercadoPagoCreateIntegrationInterface) => {
    await fetchWithErrors(async () => {
      const response = await mercadoPagoConnect(data)
      await mutatePaymentIntegrations(data => {
        if (data) {
          return [...data, response.data]
        }
      }, false)
    }, setErrors)
  }

  const countryData = () =>
    mercadoPagoCountryData.map(key => {
      return { id: key, caption: getSingleCountry(key, user?.dashboardLocale) }
    })

  return (
    <>
      <PrimaryButton
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault()
          setOpened(true)
        }}
        className={'font-bold'}
      >
        {t('dashboard.settings.payment_gateways.connect')}
      </PrimaryButton>
      <CreateModal
        fullCaption={t('dashboard.settings.payment_gateways.connect_payment_title', {
          payment: getPaymentProcessorTitle(PaymentProcessorEnum.MercadoPagoAR),
        })}
        caption={t('dashboard.settings.payment-gateways.mercado_pag-integration')}
        opened={opened}
        onClose={() => setOpened(false)}
        needToast={false}
        defaultValues={defaultValues}
        creatable={[
          {
            field: 'publicKey',
            onRender: (value, onChange) => (
              <FormInput
                key={'mercadopago-publicKey-create'}
                label={t('dashboard.settings.payment-gateways.api_public_key')}
                value={value}
                onChange={e => onChange(e.target.value)}
                required
                error={errors.publicKey}
              />
            ),
          },
          {
            field: 'accessToken',
            onRender: (value, onChange) => (
              <FormInput
                key={'mercadopago-accessToken-create'}
                label={t('dashboard.settings.payment-gateways.access_token')}
                value={value}
                onChange={e => onChange(e.target.value)}
                required
                error={errors.accessToken}
              />
            ),
          },
          {
            field: 'country',
            onRender: (value, onChange) => (
              <FormSelect
                key={'mercadopago-country-create'}
                label={t('dashboard.settings.payment-gateways.country')}
                data={countryData()}
                placeholder={t('dashboard.settings.payment-gateways.choose_country')}
                value={value}
                onChange={data => onChange(data)}
                required
                error={errors.country}
              />
            ),
          },
        ]}
        additionalInfo={
          <HelpCenterLink
            link={getMercadoPagoHelpPageLink(user?.dashboardLocale)}
            integration={getPaymentProcessorTitle(PaymentProcessorEnum.MercadoPagoAR)}
          />
        }
        onCreate={onCreate}
        requiredFields={['publicKey', 'accessToken', 'country']}
      />
    </>
  )
}

export default MercadoPagoCreateModal
